import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Icon } from '@icp/components';
import { withFieldWrapper } from '../../fieldWrapper';
import { useElementDecorator, useIsInDesign } from '../../FormRenderCtx';
import { useClassName, useVariablePattern } from '../../hooks';
import './AudioElement.css';
import AudioProgressTrack from './AudioProgressTrack';
import AudioVolumeControl from './AudioVolumeControl';
import useAudioMarkers from './useAudioMarkers';

const AudioElement = forwardRef(function AudioElement(props, ref) {
  const { keyPath, id, className: classNameProp, style, componentProps = {} } = props;

  const {
    src: srcProp = null,
    sources = [],
    markers: markersProp = null,
    markersDataSource = {},
    markersUseDataSource = false,
    style: componentStyle,
    appearance,
    autoPlay,
    loop,
    muted,
    showVolumeControl = true,
    showTrack = true,
    ...otherComponentProps
  } = componentProps;

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const audioRef = useRef(null);

  const ElementDecorator = useElementDecorator();
  const isInDesign = useIsInDesign();
  const className = useClassName(classNameProp);
  const classNameComp = useClassName(componentProps.className);
  const nodeRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      node: nodeRef.current,
    }),
    [],
  );

  const src = useVariablePattern(srcProp);
  const markers = useAudioMarkers({
    markersProp,
    markersDataSource,
    markersUseDataSource,
    duration,
    isInDesign,
  });

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = useCallback((timeValue) => {
    const time = Number(timeValue);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  }, []);

  const handleUpdateVolume = useCallback((volumeValue) => {
    setVolume(volumeValue);
    audioRef.current.volume = volumeValue;
  }, []);

  useEffect(() => {
    // autoPlay 属性在浏览器中会被限制，不一定能生效
    if (autoPlay && audioRef.current && src) {
      audioRef.current.play().catch((err) => {
        console.warn('Autoplay was prevented. User interaction may be required.', err);
        setIsPlaying(false);
      });
    }
  }, [autoPlay, src]);

  useEffect(() => {
    const initialVolume = muted ? 0 : 1;
    handleUpdateVolume(initialVolume);
  }, [handleUpdateVolume, muted]);

  return (
    <ElementDecorator keyPath={keyPath} id={id}>
      <div
        className={clsx(
          'audio-element',
          appearance === 'dark' ? 'audio-element-dark' : 'audio-element-light',
          className,
        )}
        style={style}
        ref={nodeRef}
      >
        <audio
          {...otherComponentProps}
          ref={audioRef}
          src={src}
          autoPlay={autoPlay}
          loop={loop}
          muted={volume === 0}
          onTimeUpdate={() => setCurrentTime(audioRef.current.currentTime)}
          onLoadedMetadata={() => setDuration(audioRef.current.duration)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => !loop && setIsPlaying(false)}
          controls={false}
        >
          {sources.map((item) => (
            <source key={item.src} src={item.src} type={item.type} />
          ))}
        </audio>

        <div className={clsx('controls-row', classNameComp)} style={componentStyle}>
          <button className="play-pause-btn" onClick={togglePlay} disabled={isInDesign}>
            {isPlaying ? (
              <Icon name="material:pause-rounded" size={24} />
            ) : (
              <Icon name="material:play-arrow-rounded" size={24} />
            )}
          </button>

          {showTrack ? (
            <AudioProgressTrack
              currentTime={currentTime}
              duration={duration}
              appearance={appearance}
              markers={markers}
              onSeek={handleSeek}
            />
          ) : null}

          {showVolumeControl ? (
            <AudioVolumeControl
              volume={volume}
              onUpdateVolume={handleUpdateVolume}
              appearance={appearance}
              isInDesign={isInDesign}
            />
          ) : null}
        </div>
      </div>
    </ElementDecorator>
  );
});

AudioElement.propTypes = {
  id: PropTypes.string,
  keyPath: PropTypes.string,
  className: PropTypes.string,
  componentProps: PropTypes.shape({
    /**
     * 音频的播放源
     */
    src: PropTypes.string,
    /**
     * 兼容不同的浏览器中使用
     */
    sources: PropTypes.arrayOf(
      PropTypes.shape({
        src: PropTypes.string,
        type: PropTypes.string,
      }),
    ),
    /**
     * 音频上的标记点数组，可以在播放轨道上展示，例子 [{"time": 100, "label": "marker" }]
     */
    markers: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.string]),
    /**
     * 音频上的标记点数组的数据源配置
     */
    markersDataSource: PropTypes.shape({}),
    /**
     * 音频上的标记点数组是否使用数据源配置
     */
    markersUseDataSource: PropTypes.bool,
    /**
     * 外观，可选 浅色外观 light 或者 深色外观 dark
     */
    appearance: PropTypes.string,
    /**
     * 是否自动播放，此属性即使设置了也很可能会被浏览器限制而不生效
     */
    autoPlay: PropTypes.bool,
    /**
     * 是否循环播放
     */
    loop: PropTypes.bool,
    /**
     * 是否默认静音
     */
    muted: PropTypes.bool,
    /**
     * 是否展示音量控制
     */
    showVolumeControl: PropTypes.bool,
    /**
     * 是否展示播放轨道
     */
    showTrack: PropTypes.bool,
  }),
};
// for @icp/utils/getComponentDisplayName, otherwise, in production mode, function name will be compressed.
AudioElement.displayName = 'Audio';

export default withFieldWrapper(AudioElement);
